import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Conversation } from "module/chatTypes";
import "sass/components/chat/ChatTabs.scss";

interface ChatTabsProps {
  activeTab: "public" | "private";
  onTabChange: (tab: "public" | "private") => void;
  unreadPrivateCount: number;
  conversations: Conversation[];
  activeChatKey: string | null;
  onConversationSelect: (conversationId: string) => void;
  onNewConversation: () => void;
}

export const ChatTabs: React.FC<ChatTabsProps> = ({
  activeTab,
  onTabChange,
  unreadPrivateCount,
  conversations,
  activeChatKey,
  onConversationSelect,
  onNewConversation,
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handlePrivateClick = () => {
    if (activeTab === "private") {
      // 이미 private 탭이면 드롭다운 토글
      setIsDropdownOpen(!isDropdownOpen);
    } else {
      // public에서 전환 시 private로 변경하고 드롭다운 열기
      onTabChange("private");
      setIsDropdownOpen(true);
    }
  };

  const handleConversationSelect = (id: string) => {
    onConversationSelect(id);
    setIsDropdownOpen(false);
  };

  const handleNewConversation = () => {
    onNewConversation();
    setIsDropdownOpen(false);
  };

  // 현재 선택된 대화방 이름 표시
  const activeConversation = conversations.find((c) => c.id === activeChatKey);
  const privateTabLabel = activeConversation
    ? `${t("privateChat")} (${activeConversation.participantNames.join(", ")})`
    : t("privateChat");

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className="chat-tabs">
      <button
        className={`tab ${activeTab === "public" ? "active" : ""}`}
        onClick={() => {
          onTabChange("public");
          setIsDropdownOpen(false);
        }}
      >
        {t("publicChat")}
      </button>

      <div className="private-tab-wrapper" ref={dropdownRef}>
        <button
          className={`tab ${activeTab === "private" ? "active" : ""}`}
          onClick={handlePrivateClick}
        >
          {privateTabLabel}
          <i
            className={`fas fa-chevron-${
              isDropdownOpen ? "up" : "down"
            } ml-1`}
          />
          {unreadPrivateCount > 0 && (
            <span className="unread-badge">{unreadPrivateCount}</span>
          )}
        </button>

        {/* 드롭다운 */}
        {isDropdownOpen && (
          <div className="conversation-dropdown">
            <button
              className="dropdown-item new-conversation"
              onClick={handleNewConversation}
            >
              + {t("newConversation")}
            </button>

            {conversations.length === 0 ? (
              <div className="dropdown-empty">{t("noConversations")}</div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.id}
                  className={`dropdown-item ${
                    conv.id === activeChatKey ? "active" : ""
                  }`}
                  onClick={() => handleConversationSelect(conv.id)}
                >
                  <span className="participant-names">
                    {conv.participantNames.join(", ")}
                  </span>
                  {conv.unreadCount > 0 && (
                    <span className="unread-badge">{conv.unreadCount}</span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
